import { BlockSide, LayoutObjectType } from '@yard/app/api'
import { ACCESS_POINT_BAR_THICKNESS } from '@yard/pages/YardSetup/helpers/block-access-point-bar.utils'
import { LayoutObjectWithActions } from '@yard/pages/YardSetup/models/yard-view.model'
import { Node } from 'reactflow'

export const getLayoutObjectRoadNodePosition = (
  layoutObjectData: LayoutObjectWithActions,
  referencePoint: { x: number; y: number },
): { x: number; y: number } => {
  const gateX = layoutObjectData.layoutPosition.x
  const gateY = layoutObjectData.layoutPosition.y
  const gw = layoutObjectData.width
  const gh = layoutObjectData.height
  const centerX = gateX + gw / 2
  const centerY = gateY + gh / 2

  const dx = referencePoint.x - centerX
  const dy = referencePoint.y - centerY
  const normDx = gw > 0 ? dx / (gw / 2) : dx
  const normDy = gh > 0 ? dy / (gh / 2) : dy

  if (Math.abs(normDy) >= Math.abs(normDx)) {
    return { x: centerX, y: dy < 0 ? gateY : gateY + gh }
  }
  return { x: dx < 0 ? gateX : gateX + gw, y: centerY }
}

/**
 * Computes the flow-coordinate position for a road node connected to an access point.
 *
 * `barFlowCenter` is the centre of the access point bar already expressed in flow
 * coordinates (convert from the bar's DOM bounding rect with screenToFlowPosition so
 * that CSS padding, zoom, and viewport offsets are all accounted for).
 *
 * The node is placed at the bar's outer edge — half a bar-thickness away from the centre
 * in the outward direction — and centred on the bar's face axis.
 */
export function getAccessPointRoadNodePosition(
  side: BlockSide,
  barFlowCenter: { x: number; y: number },
): { x: number; y: number } {
  const off = ACCESS_POINT_BAR_THICKNESS / 2

  switch (side) {
    case 'North':
      return { x: Math.round(barFlowCenter.x), y: Math.round(barFlowCenter.y - off - 25) }
    case 'South':
      return { x: Math.round(barFlowCenter.x), y: Math.round(barFlowCenter.y + off + 15) }
    case 'West':
      return { x: Math.round(barFlowCenter.x - off - 25), y: Math.round(barFlowCenter.y) }
    case 'East':
      return { x: Math.round(barFlowCenter.x + off + 15), y: Math.round(barFlowCenter.y) }
  }
}

const ROAD_CONNECTABLE_TYPES = new Set<string>([
  LayoutObjectType.Gate,
  LayoutObjectType.HoldingPoint,
  LayoutObjectType.HandoverArea,
])

export const isRoadConnectableLayoutObject = (node: Node) =>
  (node.type === 'gate' || node.type === 'layoutObject') &&
  ROAD_CONNECTABLE_TYPES.has((node.data as LayoutObjectWithActions).type)
